import lodash from "lodash";
import {
    createFieldCheckbox, createFieldDate,
    createFieldInput,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTemplateBuilder, createFieldTextArea
} from "../../../../../components/input/template-builder/template-builder.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, putField } from "../../../../../utils/form";
import { defineAsyncComponent } from "vue";
import moment from "moment";
import {
    callMainObjective,
    callSubOperation
} from "../../../haz-discuss/request/haz-discuss-info/haz-discuss-info.section";
import { callHazSpecies } from "../../../inform/request/inform-product/inform-product.logic";
import { Divider } from "ant-design-vue";

const Address = defineAsyncComponent(() => import("../../../../../components/address/Address"));
const CardBorder = defineAsyncComponent(() => import("../../../../../components/card/CardBorder"));
const ToWhomInformTable = defineAsyncComponent(() => import("./ToWhomInformTable"));
const ToWhomData = defineAsyncComponent(() => import("./ToWhomData"));

export const callToWhomCountry = ({ model }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true }
    ];
    where.push(
        { openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 1
            { name: "refHazDiscuss.id", type: "UUID", operator: "=", value: model?.refHazDiscuss?.id },
            { name: "refObjective.key", type: "String", operator: "=", value: model?.refObjective?.key },
            { name: "refStatus.key", type: "String", operator: "!=", value: "cancel" },
        { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" } // 1
    );

    return store
        .dispatch("resource/v1/transaction/toWhom/search", {
            criteria: {
                isDistinct: true,
                select: ["refCountry.nameEnS"],
                page: 0,
                size: 1000000,
                where,
            }
        })
        .then(page => page.data.map(items => model.countries.push(items.refCountry.nameEnS)));
};

export const callHazSubStanceType = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "HAZ_SUB_STANCE_TYPE", value: searchQuery })
        .then(items => items.filter(d => ["chemi", "micro"].includes(d.key)));

export const callHazDiscuss = ({ searchQuery, userCompanyId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "refUserCompany.id", type: "UUID", operator: "=", value: userCompanyId },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
        { name: "refResult.isToWhom", type: "Boolean", operator: "=", value: true },
    ];
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/hazDiscuss/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "approveCode"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }]
            }
        })
        .then(page => page.data);
};

export const callHazDiscussFull = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/hazDiscuss/search", {
            criteria: {
                select: ["**", "refUserCompany.refPersonType.*", "refUser.refPersonType.*","refResult.**"],
                page: 0,
                size: 1,
                where: [{ name: "id", type: "UUID", operator: "=", value: id }],
                children: [
                    {
                        name: "requests",
                        asName: "lastRequestApprove",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                        sort: [{ name: "createDate", type: "DESC" }],
                        children: [
                            {
                                name: "statuses",
                                asName: "lastStatusApprove",
                                select: ["**"],
                                page: 0,
                                size: 1,
                                where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                                sort: [{ name: "createDate", type: "DESC" }]
                            }
                        ]
                    },
                    {
                        name: "attachments",
                        select: ["*"],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "ASC" }]
                    },
                ]
            }
        })
        .then(page => page.data[0]);
};

export const createField = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldTemplateBuilder({
            colProp: () => ({ xs: 24, md: 12 }),
            inputProp: () => ({
                useForm: useForm,
                fields: [
                    createFieldSelect({
                        visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                        key: `${prefix}refHazDiscuss`,
                        colProp: () => ({ xs: 24 }),
                        formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                        label: () => i18n.global.t("model.toWhom.refHazDiscuss"),
                        inputProp: ({ field }) =>
                            lodash.merge(
                                {
                                    value: model.refHazDiscuss,
                                    "onUpdate:value": value => {
                                        model.toWhomProducts = []
                                        model.countries = []
                                        if (value) {
                                            callHazDiscussFull({ id: value.id }).then(hazDiscuss => {
                                                model.refHazDiscuss = hazDiscuss;
                                                if(["Other"].includes(hazDiscuss.refSubObjective?.value2)){
                                                    model.refHazDiscuss.refSubObjective.value = model.refHazDiscuss.otherSubObjective
                                                }
                                                if(["Other"].includes(hazDiscuss.refDiscussTopic?.value2)){
                                                    model.refHazDiscuss.refDiscussTopic.value = model.refHazDiscuss.otherDiscussTopic
                                                }
                                                if(["Other"].includes(hazDiscuss.refToUse?.value2)){
                                                    model.refHazDiscuss.refToUse.value = model.refHazDiscuss.otherToUse
                                                }
                                                hazDiscuss.productType = model.refHazDiscuss.refToUse.value2
                                                model.toWhomProducts.push({
                                                    refHazDiscuss: hazDiscuss
                                                });
                                                callToWhomCountry({ model })
                                            });
                                        } else {
                                            model.refHazDiscuss = null;
                                            model.toWhomProducts = [];
                                            model.countries = [];
                                        }
                                    },
                                    disabled: viewOnly || !model.refUserCompany?.id,
                                    list: ({ searchQuery }) => callHazDiscuss({ searchQuery, userCompanyId: model.refUserCompany.id, isOfficerCreate: model.isOfficerCreate }),
                                    optLabel: "approveCode"
                                },
                                conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                            ),
                        changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                    }),
                    createFieldInput({
                        visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                        key: `${prefix}refHazDiscuss.refMainOperation`,
                        colProp: () => ({ xs: 24 }),
                        formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                        label: () => i18n.global.t("model.toWhom.refMainOperation"),
                        inputProp: ({ field }) =>
                            lodash.merge(
                                {
                                    value: model.refHazDiscuss?.refMainOperation?.value,
                                    "onUpdate:value": value => (model.refHazDiscuss.refMainOperation = value),
                                    disabled: viewOnly
                                },
                                conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                            )
                    }),
                    createFieldSelect({
                        visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                        key: `${prefix}refHazDiscuss.refMainObjective`,
                        colProp: () => ({ xs: 24 }),
                        formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                        label: () => i18n.global.t("model.toWhom.refMainObjective"),
                        inputProp: ({ field }) =>
                            lodash.merge(
                                {
                                    value: model.refHazDiscuss?.refMainObjective,
                                    "onUpdate:value": value => (model.refHazDiscuss.refMainObjective = value),
                                    disabled: viewOnly,
                                    list: callMainObjective,
                                    optLabel: "value"
                                },
                                conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                            ),
                        changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                    }),
                    createFieldSelect({
                        visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                        key: `${prefix}refHazDiscuss.refResult.refSpecies`,
                        colProp: () => ({ xs: 24 }),
                        formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                        label: () => `${i18n.global.t("model.hazObject.refSpecies")}`,
                        inputProp: ({ field }) =>
                            lodash.merge(
                                {
                                    value: model.refHazDiscuss?.refResult?.refSpecies,
                                    "onUpdate:value": value => model.refHazDiscuss.refResult.refSpecies = value,
                                    disabled: viewOnly,
                                    list: callHazSpecies,
                                    optLabel: "value"
                                },
                                conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                            ),
                        changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                    }),
                ],
                skeleton: isLoading.value
            })
        }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldDate({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.lastRequestApprove.0.lastStatusApprove.0.statusDatetime`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.toWhom.approveDate"),
                            inputProp: ({ field }) => {
                                const datetime = model.refHazDiscuss?.lastRequestApprove?.[0]?.lastStatusApprove?.[0]?.statusDatetime;
                                return lodash.merge(
                                    {
                                        value: datetime ? moment(datetime).format("YYYY-MM-DD") : null,
                                        disabled: true
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                );
                            }
                        }),
                        createFieldSelect({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.refSubOperation`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.toWhom.refSubOperation"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.refSubOperation,
                                        "onUpdate:value": value => (model.refHazDiscuss.refSubOperation = value),
                                        disabled: viewOnly,
                                        list: callSubOperation,
                                        optLabel: "value"
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.refSubObjective`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.toWhom.refSubObjective"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.refSubObjective?.value,
                                        "onUpdate:value": value => (model.refHazDiscuss.refSubObjective = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                    ],
                    skeleton: isLoading.value
                })
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refHazDiscuss.refDiscussTopic`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.hazDiscuss.refDiscussTopic"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refHazDiscuss?.refDiscussTopic?.value,
                            "onUpdate:value": value => (model.refHazDiscuss.refDiscussTopic = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.tradeNameTh`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.haz.tradeNameTh"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.tradeNameTh,
                                        "onUpdate:value": value => (model.refHazDiscuss.tradeNameTh = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.customsCoordinates`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscuss.customsCoordinates"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.customsCoordinates,
                                        "onUpdate:value": value => (model.refHazDiscuss.customsCoordinates = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),

                    ],
                    skeleton: isLoading.value
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.tradeNameEn`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.haz.tradeNameEn"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.tradeNameEn,
                                        "onUpdate:value": value => (model.refHazDiscuss.tradeNameEn = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.manufacturer`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.haz.manufacturer"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.manufacturer,
                                        "onUpdate:value": value => (model.refHazDiscuss.manufacturer = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                    ],
                    skeleton: isLoading.value
                })
            }),
            {
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}addresses.0`,
                colProp: () => ({ xs: 24, class: "mt-3" }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    if (!model.addresses?.some(d => d.isDelete !== true)) {
                        return null;
                    }
                    return (
                        <>
                            <div class="text-bold text-16">{i18n.global.t("views.request.hcInfo.manufactureAddressEn")}</div>
                            <Address
                                menuContext={menuContext}
                                viewOnly={viewOnly}
                                dataProp={data}
                                dataKey={`${prefix}addresses.0`}
                                isLoading={isLoading.value}
                                locale={[{ name: "En", suffix: true }]}
                                onUpdateSome={() => (model.isSave = true)}
                            ></Address>
                        </>
                    );
                }
            },
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldRadioGroup({
                            visible: ({ field }) => conditionFormChecker({
                                props,
                                data,
                                executeKey: "visibleField",
                                params: { field }
                            }),
                            key: `${prefix}refHazDiscuss.refSubStanceTypeMain`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.haz.refSubStanceTypeMain"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.refSubStanceTypeMain,
                                        "onUpdate:value": value => {
                                            const oldValue = model.refHazDiscuss?.refSubStanceTypeMain;
                                            model.refHazDiscuss.refSubStanceTypeMain = value;
                                            if (
                                                (["micro"].includes(oldValue?.key) && !["micro"].includes(value?.key)) ||
                                                (!["micro"].includes(oldValue?.key) && ["micro"].includes(value?.key))
                                            )
                                            if (!["chemi"].includes(value?.key)) {
                                                model.refHazDiscuss.isTechnicalGrade = false;
                                            }
                                        },
                                        disabled: viewOnly,
                                        list: callHazSubStanceType,
                                        optLabel: "value"
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field }
                                    })
                                ),
                            changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
                        }),
                        createFieldCheckbox({
                            visible: ({ field }) => conditionFormChecker({
                                props,
                                data,
                                executeKey: "visibleField",
                                params: { field }
                            }),
                            key: `${prefix}refHazDiscuss.isTechnicalGrade`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({
                                labelCol: { xs: 24, md: 8 },
                                wrapperCol: { xs: 24, md: 16 },
                                colon: false
                            }),
                            label: () => i18n.global.t("model.haz.isTechnicalGrade"),
                            labelRender: () => " ",
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        checked: model.refHazDiscuss?.isTechnicalGrade,
                                        "onUpdate:checked": checked => (model.refHazDiscuss.isTechnicalGrade = checked),
                                        disabled: true,
                                        text: () => i18n.global.t("model.haz.isTechnicalGrade")
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "checkboxField",
                                        params: { field }
                                    })
                                )
                        }),
                        createFieldTemplateBuilder({
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("views.request.hazInfo.nature"),
                            inputProp: () => ({
                                useForm: useForm,
                                fields: [
                                    createFieldCheckbox({
                                        visible: ({ field }) => conditionFormChecker({
                                            props,
                                            data,
                                            executeKey: "visibleField",
                                            params: { field }
                                        }),
                                        key: `${prefix}refHazDiscuss.isSolid`,
                                        colProp: () => ({ xs: 12 }),
                                        formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                                        label: () => i18n.global.t("model.haz.isSolid"),
                                        labelRender: () => "",
                                        inputProp: ({ field }) =>
                                            lodash.merge(
                                                {
                                                    checked: model.refHazDiscuss?.isSolid,
                                                    "onUpdate:checked": checked => {
                                                        model.refHazDiscuss.isSolid = checked;
                                                        if (checked) {
                                                            model.refHazDiscuss.isLiquid = false;
                                                            model.refHazDiscuss.liquid = null;
                                                        }
                                                    },
                                                    disabled: true,
                                                    text: () => i18n.global.t("model.haz.isSolid")
                                                },
                                                conditionFormChecker({
                                                    props,
                                                    data,
                                                    executeKey: "checkboxField",
                                                    params: { field }
                                                })
                                            )
                                    }),
                                    createFieldInput({
                                        visible: ({ field }) => conditionFormChecker({
                                            props,
                                            data,
                                            executeKey: "visibleField",
                                            params: { field }
                                        }),
                                        key: `${prefix}refHazDiscuss.solid`,
                                        colProp: () => ({ xs: 12 }),
                                        formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                                        label: () => i18n.global.t("model.haz.solid"),
                                        labelRender: () => "",
                                        inputProp: ({ field }) =>
                                            lodash.merge(
                                                {
                                                    value: model.refHazDiscuss?.solid,
                                                    "onUpdate:value": value => (model.refHazDiscuss.solid = value),
                                                    disabled: viewOnly || !model.refHazDiscuss?.isSolid
                                                },
                                                conditionFormChecker({
                                                    props,
                                                    data,
                                                    executeKey: "inputPropField",
                                                    params: { field }
                                                })
                                            )
                                    }),
                                    createFieldCheckbox({
                                        visible: ({ field }) => conditionFormChecker({
                                            props,
                                            data,
                                            executeKey: "visibleField",
                                            params: { field }
                                        }),
                                        key: `${prefix}refHazDiscuss.isLiquid`,
                                        colProp: () => ({ xs: 12 }),
                                        formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                                        label: () => i18n.global.t("model.haz.isLiquid"),
                                        labelRender: () => "",
                                        inputProp: ({ field }) =>
                                            lodash.merge(
                                                {
                                                    checked: model.refHazDiscuss?.isLiquid,
                                                    "onUpdate:checked": checked => {
                                                        model.refHazDiscuss.isLiquid = checked;
                                                        if (checked) {
                                                            model.refHazDiscuss.isSolid = false;
                                                            model.refHazDiscuss.solid = null;
                                                        }
                                                    },
                                                    disabled: true,
                                                    text: () => i18n.global.t("model.haz.isLiquid")
                                                },
                                                conditionFormChecker({
                                                    props,
                                                    data,
                                                    executeKey: "checkboxField",
                                                    params: { field }
                                                })
                                            )
                                    }),
                                    createFieldInput({
                                        visible: ({ field }) => conditionFormChecker({
                                            props,
                                            data,
                                            executeKey: "visibleField",
                                            params: { field }
                                        }),
                                        key: `${prefix}refHazDiscuss.liquid`,
                                        colProp: () => ({ xs: 12 }),
                                        formItemProp: () => ({ wrapperCol: { xs: 24 } }),
                                        label: () => i18n.global.t("model.haz.liquid"),
                                        labelRender: () => "",
                                        inputProp: ({ field }) =>
                                            lodash.merge(
                                                {
                                                    value: model.refHazDiscuss?.liquid,
                                                    "onUpdate:value": value => (model.refHazDiscuss.liquid = value),
                                                    disabled: viewOnly || !model.refHazDiscuss?.isLiquid
                                                },
                                                conditionFormChecker({
                                                    props,
                                                    data,
                                                    executeKey: "inputPropField",
                                                    params: { field }
                                                })
                                            )
                                    })
                                ],
                                skeleton: isLoading.value
                            })
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.refToUse`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscuss.refToUse"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.refToUse?.value,
                                        "onUpdate:value": value => (model.refHazDiscuss.refToUse.value = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.otherToUse`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.hazDiscuss.otherToUse"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.otherToUse,
                                        "onUpdate:value": value => (model.refHazDiscuss.otherToUse = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                    ]
                })
            }),
            createFieldTemplateBuilder({
                colProp: () => ({ xs: 24, md: 12 }),
                inputProp: () => ({
                    useForm: useForm,
                    fields: [
                        createFieldInput({
                            visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                            key: `${prefix}refHazDiscuss.refCountry`,
                            colProp: () => ({ xs: 24 }),
                            formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                            label: () => i18n.global.t("model.toWhom.refCountry"),
                            inputProp: ({ field }) =>
                                lodash.merge(
                                    {
                                        value: model.refHazDiscuss?.refCountry?.nameTh,
                                        "onUpdate:value": value => (model.refHazDiscuss.refCountry = value),
                                        disabled: viewOnly
                                    },
                                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                                )
                        }),
                    ]
                })
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refHazDiscuss.refResult`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.toWhom.refResult"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refHazDiscuss?.refResult?.answer,
                            "onUpdate:value": value => (model.refHazDiscuss.refResult = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemRender: () => <Divider></Divider>
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("model.freeSale.refInform.approveCode")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { componentKey: "ToWhomInformTable" } })
                    );
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}refToWhomTemp`,
                            visiblePagination: false,
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "ToWhomInformTable" } })
                    );
                    return () => {
                        model?.refInform;
                        return model?.refHazDiscuss?.refResult?.isFact && model.refObjective?.key === '2' ?
                            <>
                                <CardBorder {...cardProp}>
                                    <ToWhomInformTable{...componentProp}></ToWhomInformTable>
                                </CardBorder>
                            </> : <span></span>
                    }
                }
            },
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const cardProp = lodash.merge(
                        {
                            title: i18n.global.t("views.request.informProduct.title")
                        },
                        conditionFormChecker({ props, data, executeKey: "cardProp", params: { componentKey: "ToWhomData" } })
                    );
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "ToWhomData" } })
                    );
                    return () => {
                        model?.toWhomProducts;
                        return !model?.toWhomProducts?.length ? <span></span> : <>
                            <CardBorder {...cardProp}>
                                <ToWhomData {...componentProp}></ToWhomData>
                            </CardBorder>
                        </>
                    }
                }
            },
        ]
    });
};